/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Spin,
  Modal,
  DatePicker,
  Input,
  Descriptions,
  Tag,
  Empty,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import { getTiposDeMovimientoDePersonal } from 'api/catalogos/tiposDeMovimientoDePersonal';
import { postObtenerReporte } from 'api/reporteador';
import {
  getMovimientosDePersonal,
  autorizarMovimientoDePersonal,
  cancelarMovimientoDePersonal,
  generarBackfillMovimientos,
} from 'api/entidad/movimientosDePersonal';
import { checkToken } from 'api/utils';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_CANCELADO,
} from 'utils/constants';

const { RangePicker } = DatePicker;

export const movimientodepersonalPermission = {
  permissionModel: 'movimientodepersonal',
  catalogsModels: [],
  tabsModels: [],
};

const formatFecha = (val) => {
  if (!val) return '-';
  if (val._isAMomentObject || typeof val?.format === 'function') return val.format('DD/MM/YYYY');
  return val;
};

const MovimientosDePersonal = ({ permission }) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [tiposMovimiento, setTiposMovimiento] = useState([]);
  const [selectedData, setSelectedData] = useState({});
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [visibleCancelar, setVisibleCancelar] = useState(false);
  const [visibleGenerar, setVisibleGenerar] = useState(false);
  const [visibleDetalle, setVisibleDetalle] = useState(false);
  const [visibleReporte, setVisibleReporte] = useState(false);
  const [base64, setBase64] = useState(null);
  const [filtroTipoMovimiento, setFiltroTipoMovimiento] = useState(undefined);
  const [filtroFechas, setFiltroFechas] = useState(null);

  const fetchData = async (params = {}) => {
    setLoading(true);
    const queryParams = { ...params };
    if (filtroTipoMovimiento) {
      queryParams.tipo_de_movimiento_de_personal_id = filtroTipoMovimiento;
    }
    if (filtroFechas && filtroFechas[0] && filtroFechas[1]) {
      queryParams.fecha_movimiento_desde = filtroFechas[0].format('YYYY-MM-DD');
      queryParams.fecha_movimiento_hasta = filtroFechas[1].format('YYYY-MM-DD');
    }
    const _data = await getMovimientosDePersonal(queryParams);
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [_estados, _tipos, _data] = await Promise.all([
        getEstadosGlobales(),
        getTiposDeMovimientoDePersonal(),
        getMovimientosDePersonal(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setTiposMovimiento(_tipos);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisibleAutorizar(false);
    setVisibleCancelar(false);
    setVisibleGenerar(false);
    setVisibleDetalle(false);
    setVisibleReporte(false);
    setSelectedRowKeys([]);
    setSelectedData({});
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) { setLoading(false); return; }
      const saved = await autorizarMovimientoDePersonal(key);
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onConfirmCancelar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) { setLoading(false); return; }
      const saved = await cancelarMovimientoDePersonal(key);
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onConfirmGenerar = async () => {
    try {
      setLoading(true);
      const result = await generarBackfillMovimientos();
      if (result) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys((prevKeys) => (prevKeys.includes(record.id)
      ? prevKeys.filter((k) => k !== record.id)
      : [...prevKeys, record.id]));
    setSelectedData({ ...record });
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'checkbox',
    onSelect: (record, selected) => {
      if (selected) {
        setSelectedRowKeys([...selectedRowKeys, record.id]);
      } else {
        setSelectedRowKeys(selectedRowKeys.filter((k) => k !== record.id));
      }
    },
    onSelectAll: (selected) => {
      setSelectedRowKeys(selected ? data.map((row) => row.id) : []);
    },
  };

  const onCancelReporte = () => {
    setVisibleReporte(false);
    setBase64(null);
  };

  const onClickPreview = async () => {
    if (selectedRowKeys.length === 0) return;
    setLoading(true);
    try {
      const values = {
        ID: 7000,
        Parametros: [
          {
            Nombre: 'movimientos_por_id',
            TipoParametro: 'EnteroLista',
            Valores: selectedRowKeys.map((id) => ({ Value: id })),
          },
        ],
      };
      const file = await postObtenerReporte(values);
      if (file) {
        setBase64(file);
        setVisibleReporte(true);
      } else {
        message.error('Error al generar el reporte. Inténtalo de nuevo más tarde.');
      }
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      titleText: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      titleText: 'Tipo de Movimiento',
      dataIndex: 'tipo_de_movimiento_de_personal_concepto',
      key: 'tipo_de_movimiento_de_personal_concepto',
      width: 250,
    },
    {
      titleText: 'Empleado',
      dataIndex: 'numero_y_nombre_completo',
      key: 'numero_y_nombre_completo',
      width: 300,
    },
    {
      titleText: 'Plaza',
      dataIndex: 'plaza_clave_y_descripcion',
      key: 'plaza_clave_y_descripcion',
      width: 250,
    },
    {
      titleText: 'Puesto',
      dataIndex: 'puesto_clave_y_descripcion',
      key: 'puesto_clave_y_descripcion',
      width: 250,
    },
    {
      titleText: 'Adscripcion',
      dataIndex: 'directorio_de_area_clave_y_nombre',
      key: 'directorio_de_area_clave_y_nombre',
      width: 250,
    },
    {
      titleText: 'Fecha de Movimiento',
      dataIndex: 'fecha_movimiento',
      key: 'fecha_movimiento',
      width: 160,
      render: formatFecha,
    },
    {
      titleText: 'Sueldo Diario',
      dataIndex: 'sueldo_diario',
      key: 'sueldo_diario',
      width: 140,
      align: 'right',
    },
    {
      titleText: 'Sueldo Mensual',
      dataIndex: 'sueldo_mensual',
      key: 'sueldo_mensual',
      width: 140,
      align: 'right',
    },
    {
      titleText: 'Grupo',
      dataIndex: 'grupo_movimiento',
      key: 'grupo_movimiento',
      width: 100,
      render: (val) => (val
        ? <Tag color="blue">Doble</Tag>
        : <Tag>Unico</Tag>),
    },
    {
      titleText: 'Antes/Despues',
      dataIndex: 'es_movimiento_anterior',
      key: 'es_movimiento_anterior',
      width: 130,
      render: (val, record) => {
        if (!record.grupo_movimiento) return '-';
        return val ? <Tag color="orange">Antes</Tag> : <Tag color="green">Despues</Tag>;
      },
    },
    {
      titleText: 'Tipo de Operación',
      dataIndex: 'tipo_operacion',
      key: 'tipo_operacion',
      width: 160,
    },
    {
      titleText: 'Fecha de Autorización',
      dataIndex: 'fecha_autorizacion',
      key: 'fecha_autorizacion',
      width: 180,
      render: formatFecha,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales_id',
      key: 'estados_globales_id',
      width: 150,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const esActivo = selectedData?.estados_globales_id === ESTATUS_ACTIVO;
  const esAutorizado = selectedData?.estados_globales_id === ESTATUS_AUTORIZADO;

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Row gutter={[10, 10]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <Select
              labelProp="concepto"
              keyProp="id"
              dataSource={tiposMovimiento}
              placeholder="Filtrar por Tipo de Movimiento"
              allowClear
              value={filtroTipoMovimiento}
              onChange={(val) => {
                setFiltroTipoMovimiento(val);
              }}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <RangePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              placeholder={['Fecha Desde', 'Fecha Hasta']}
              value={filtroFechas}
              onChange={(dates) => setFiltroFechas(dates)}
            />
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Input.Search
              placeholder="Buscar..."
              enterButton="Filtrar"
              onSearch={() => fetchData()}
              allowClear
            />
          </Col>
        </Row>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd: false,
            onClickEdit: () => setVisibleDetalle(true),
            onClickDelete: false,
          }}
          customActions={[
            {
              onClick: () => setVisibleAutorizar(true),
              icon: <CheckCircleOutlined />,
              text: 'Autorizar',
              permission: { model: 'movimientodepersonal', action: 'autorizar' },
              disabledBySelection: true,
              disabled: !esActivo || selectedRowKeys.length > 1,
              className: 'success',
            },
            {
              onClick: () => setVisibleCancelar(true),
              icon: <CloseCircleOutlined />,
              text: 'Cancelar',
              permission: { model: 'movimientodepersonal', action: 'cancelar' },
              disabledBySelection: true,
              disabled: (!esActivo && !esAutorizado) || selectedRowKeys.length > 1,
              className: 'danger',
            },
            {
              onClick: () => setVisibleGenerar(true),
              icon: <SyncOutlined />,
              text: 'Generar Movimientos',
              permission: { model: 'movimientodepersonal', action: 'add' },
              disabledBySelection: false,
              className: 'info',
            },
            {
              onClick: onClickPreview,
              icon: <PrinterOutlined />,
              text: 'Imprimir',
              disabled: selectedRowKeys.length === 0,
            },
          ]}
          permission={permission}
          allowExportPDF={false}
        />
        <Modal
          open={visibleDetalle}
          closable
          footer={null}
          title="Detalle del Movimiento de Personal"
          onCancel={() => setVisibleDetalle(false)}
          width={800}
        >
          {selectedData?.id && (
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Tipo de Movimiento" span={2}>
                {selectedData.tipo_de_movimiento_de_personal_concepto}
              </Descriptions.Item>
              <Descriptions.Item label="Empleado" span={2}>
                {selectedData.numero_y_nombre_completo}
              </Descriptions.Item>
              <Descriptions.Item label="Plaza">
                {selectedData.plaza_clave_y_descripcion}
              </Descriptions.Item>
              <Descriptions.Item label="Puesto">
                {selectedData.puesto_clave_y_descripcion}
              </Descriptions.Item>
              <Descriptions.Item label="Adscripcion">
                {selectedData.directorio_de_area_clave_y_nombre}
              </Descriptions.Item>
              <Descriptions.Item label="Fecha de Movimiento">
                {formatFecha(selectedData.fecha_movimiento)}
              </Descriptions.Item>
              <Descriptions.Item label="Sueldo Diario">
                {selectedData.sueldo_diario}
              </Descriptions.Item>
              <Descriptions.Item label="Sueldo Mensual">
                {selectedData.sueldo_mensual}
              </Descriptions.Item>
              <Descriptions.Item label="Fecha de Alta">
                {formatFecha(selectedData.fecha_de_alta)}
              </Descriptions.Item>
              <Descriptions.Item label="Motivo de Alta">
                {selectedData.motivo_alta}
              </Descriptions.Item>
              <Descriptions.Item label="Fecha de Baja">
                {formatFecha(selectedData.fecha_de_baja)}
              </Descriptions.Item>
              <Descriptions.Item label="Motivo de Baja">
                {selectedData.motivo_baja}
              </Descriptions.Item>
              <Descriptions.Item label="Antiguedad">
                {selectedData.antiguedad_texto}
              </Descriptions.Item>
              <Descriptions.Item label="Tipo de Operación">
                {selectedData.tipo_operacion || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Fecha de Autorización">
                {formatFecha(selectedData.fecha_autorizacion)}
              </Descriptions.Item>
              <Descriptions.Item label="Estatus">
                {estados.find((e) => e.id === selectedData.estados_globales_id)?.descripcion}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Modal>
        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          contentTitle={`Autorizar el Movimiento de: ${selectedData?.numero_y_nombre_completo}`}
          open={visibleAutorizar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmCancelar}
          ConfirmCancel
          onCancel={() => setVisibleCancelar(false)}
          contentTitle={`Cancelar el Movimiento de: ${selectedData?.numero_y_nombre_completo}`}
          open={visibleCancelar}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmGenerar}
          ConfirmAutorize
          onCancel={() => setVisibleGenerar(false)}
          contentTitle="Generar Movimientos de Personal para todas las Asignaciones Autorizadas"
          open={visibleGenerar}
          loading={loading}
        />
        <Modal
          open={visibleReporte}
          closable
          footer={null}
          title="Reporte de Movimiento de Personal"
          onCancel={onCancelReporte}
          width="90%"
          style={{ top: 20 }}
          destroyOnClose
        >
          <div style={{ height: '75vh', backgroundColor: '#f5f5f5', padding: '10px' }}>
            {base64 ? (
              <iframe
                src={base64}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                }}
                title="Vista Previa de Movimientos de Personal"
              />
            ) : (
              <Empty description="No hay reporte para mostrar." style={{ marginTop: '10%' }} />
            )}
          </div>
        </Modal>
      </Spin>
    </Row>
  );
};

export default MovimientosDePersonal;
